import { ReactElement, useEffect } from 'react';
import {
  Stack,
  TextField,
  Typography,
  Avatar,
  Box,
  FormControlLabel,
  Switch,
  createFilterOptions,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruckFast as VehicleIcon,
  faVectorSquare as LogisticsZonesIcon,
} from '@fortawesome/free-solid-svg-icons';

import {
  Subscription,
  UserInfo,
  AccessFeature,
  LogisiticZoneInfo,
  CustomAutocomplete,
  VehicleInfo,
} from '~components/index';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { getDefaultAvatar } from '~utils/user';
import { PlanFeaturesTypes, Roles } from '~globals/types/enums';
import { ORDERS_FINALIZED_STATE_OPTIONS } from '~constants/ordersStatesOptions';
import { SOURCE_IMPORT_TYPES_OPTIONS } from '~constants/sourceImportType';

import { useOrdersFiltersMenuV2Context } from '../../Context';

import { OrdersFiltersMenuV2Inputs } from '../../styles';

const OrdersFiltersMenuV2VariantFinalized = (): ReactElement => {
  const {
    filters: { finalized: filtersFinalized },
    handleChangeInputFilter,
    handleChangeInputDateFilter,
    selectsData,
  } = useOrdersFiltersMenuV2Context();

  useEffect(() => {
    if (filtersFinalized.activeFilters.itemState?.length) {
      filtersFinalized.autocompletesRefs.itemState.current?.setValue(
        filtersFinalized.activeFilters.itemState,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.itemState]);

  useEffect(() => {
    if (filtersFinalized.activeFilters.vehicles?.length) {
      filtersFinalized.autocompletesRefs.vehicles.current?.setValue(
        filtersFinalized.activeFilters.vehicles,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.vehicles]);

  useEffect(() => {
    if (filtersFinalized.activeFilters.drivers?.length) {
      filtersFinalized.autocompletesRefs.drivers.current?.setValue(
        filtersFinalized.activeFilters.drivers,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.drivers]);

  useEffect(() => {
    if (filtersFinalized.activeFilters.services?.length) {
      filtersFinalized.autocompletesRefs.services.current?.setValue(
        filtersFinalized.activeFilters.services,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.services]);

  useEffect(() => {
    if (filtersFinalized.activeFilters.zones?.length) {
      filtersFinalized.autocompletesRefs.logisticsZones.current?.setValue(
        filtersFinalized.activeFilters.zones,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.zones]);

  useEffect(() => {
    if (filtersFinalized.activeFilters.assignedUser) {
      filtersFinalized.autocompletesRefs.assignedUser.current?.setValue(
        filtersFinalized.activeFilters.assignedUser,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.assignedUser]);

  useEffect(() => {
    if (filtersFinalized.activeFilters.sourceImports?.length) {
      filtersFinalized.autocompletesRefs.sourceImports.current?.setValue(
        filtersFinalized.activeFilters.sourceImports,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFinalized.activeFilters.sourceImports]);

  return (
    <OrdersFiltersMenuV2Inputs>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Precio
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Mayor a"
            name="priceGt"
            value={filtersFinalized.filters.priceGt ?? ''}
            onChange={handleChangeInputFilter(filtersFinalized)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <TextField
            label="Menor a"
            name="priceLt"
            value={filtersFinalized.filters.priceLt ?? ''}
            onChange={handleChangeInputFilter(filtersFinalized)}
            type="number"
            onKeyDown={(event) => event.stopPropagation()}
            fullWidth
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Fecha de finalización
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersFinalized.filters.finalizedDateTimeFrom}
            onChange={handleChangeInputDateFilter(filtersFinalized)(
              'finalizedDateTimeFrom',
            )}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersFinalized.filters.finalizedDateTimeTo}
            onChange={handleChangeInputDateFilter(filtersFinalized)(
              'finalizedDateTimeTo',
            )}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Stack spacing={0.5} direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            &#8288;Fecha de vencimiento
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={
                  filtersFinalized.filters.includeWithoutExpirationDate ?? false
                }
                onChange={(event) =>
                  filtersFinalized.onChangeFilterInput(
                    'includeWithoutExpirationDate',
                    event.target.checked,
                  )
                }
                size="small"
              />
            }
            label="Sin fecha de vencimiento"
            componentsProps={{
              typography: {
                variant: 'caption',
                fontSize: 10,
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersFinalized.filters.expirationDateTimeFrom}
            onChange={handleChangeInputDateFilter(filtersFinalized)(
              'expirationDateTimeFrom',
            )}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersFinalized.filters.expirationDateTimeTo}
            onChange={handleChangeInputDateFilter(filtersFinalized)(
              'expirationDateTimeTo',
            )}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          &#8288;Fecha de creación
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filtersFinalized.filters.dateFrom ?? null}
            onChange={handleChangeInputDateFilter(filtersFinalized)('dateFrom')}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filtersFinalized.filters.dateTo ?? null}
            onChange={handleChangeInputDateFilter(filtersFinalized)('dateTo')}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <CustomAutocomplete
        innerRef={filtersFinalized.autocompletesRefs.itemState}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={ORDERS_FINALIZED_STATE_OPTIONS}
        label="Estados"
        name="itemState"
        onChange={(_e, vals) => {
          filtersFinalized.onChangeFilterInput('itemState', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <CustomAutocomplete
        innerRef={filtersFinalized.autocompletesRefs.vehicles}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={`${val?.displayName}${val?.code ? ` ${val.code}` : ''}`}
              icon={
                <FontAwesomeIcon
                  icon={VehicleIcon}
                  style={{ color: val.color }}
                />
              }
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.vehicles.list}
        loading={selectsData.vehicles.loading}
        label="Vehículos"
        name="vehicles"
        onChange={(_e, vals) => {
          filtersFinalized.onChangeFilterInput('vehicles', vals);
        }}
        getOptionLabel={(option) =>
          `${option?.displayName}${option?.code ? ` ${option.code}` : ''}`
        }
        renderOption={(props, option) => (
          <li {...props}>
            <VehicleInfo
              vehicle={option.displayName}
              vehicleCode={option.code as string}
              color={option.color}
            />
          </li>
        )}
      />

      <CustomAutocomplete
        innerRef={filtersFinalized.autocompletesRefs.drivers}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              avatar={
                <Avatar
                  src={getDefaultAvatar(val.avatar ?? undefined)}
                  alt={val.displayName}
                />
              }
              label={val.displayName}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.drivers.list}
        loading={selectsData.drivers.loading}
        label="Choferes"
        name="drivers"
        onChange={(_e, vals) => {
          filtersFinalized.onChangeFilterInput('drivers', vals);
        }}
        getOptionLabel={(option) => option.displayName}
        renderOption={(props, option) => (
          <li {...props}>
            <UserInfo
              name={option.displayName}
              urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
            />
          </li>
        )}
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) => option.displayName.replace(',', ''),
        })}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersFinalized.autocompletesRefs.services}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              label={val.label.toString()}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.services.list}
        loading={selectsData.services.loading}
        label="Tipos de servicios"
        name="services"
        onChange={(_e, vals) => {
          filtersFinalized.onChangeFilterInput('services', vals);
        }}
        fullWidth
        getOptionLabel={(option) => option.label.toString()}
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.MgmZones}
        innerRef={filtersFinalized.autocompletesRefs.logisticsZones}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              icon={
                val.color ? (
                  <FontAwesomeIcon
                    icon={LogisticsZonesIcon}
                    style={{ color: val.color }}
                  />
                ) : undefined
              }
              label={val.code}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={selectsData.logisticsZones.list}
        loading={selectsData.logisticsZones.loading}
        label="Zonas logísticas"
        name="zones"
        onChange={(_e, vals) => {
          filtersFinalized.onChangeFilterInput('zones', vals);
        }}
        getOptionLabel={(option) =>
          option.description
            ? `${option.code} ${option.description}`
            : option.code
        }
        renderOption={(props, option) => (
          <li {...props}>
            <LogisiticZoneInfo
              name={option.code}
              description={option.description}
              color={option.color}
              withTooltip={Boolean(option.color)}
            />
          </li>
        )}
        fullWidth
      />

      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitNotPro}
        innerRef={filtersFinalized.autocompletesRefs.assignedUser}
        options={selectsData.assignedUser.list}
        loading={selectsData.assignedUser.loading}
        label="Usuario asignado"
        name="assignedUser"
        onChange={(_e, val) => {
          filtersFinalized.onChangeFilterInput('assignedUser', val);
        }}
        fullWidth
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.userId}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <UserInfo
              name={`${option.lastName}, ${option.firstName}`}
              urlAvatar={option.avatarUrl || undefined}
            />
          </li>
        )}
        startAdornment={(val) =>
          val && (
            <Avatar
              src={getDefaultAvatar(val.avatarUrl || undefined)}
              alt={`${val.lastName}, ${val.firstName}`}
              sx={{ width: 20, height: 20 }}
            />
          )
        }
        groupBy={(option) => {
          if (option.roleId === Roles.Drive) {
            return 'Choferes';
          }

          return 'Usuarios';
        }}
        filterOptions={createFilterOptions({
          ignoreAccents: true,
          ignoreCase: true,
          stringify: (option) =>
            `${option.lastName}, ${option.firstName}`.replace(',', ''),
        })}
      />

      <CustomAutocomplete
        innerRef={filtersFinalized.autocompletesRefs.sourceImports}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              avatar={
                <Box
                  component="img"
                  src={val.image.url}
                  alt={val.image.alt}
                  sx={{ height: 18, width: 18, objectFit: 'contain' }}
                />
              }
              label={val.label}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={SOURCE_IMPORT_TYPES_OPTIONS}
        label="Medios de importación"
        name="sourceImports"
        onChange={(_e, vals) => {
          filtersFinalized.onChangeFilterInput('sourceImports', vals);
        }}
        getOptionLabel={(option) => option.label.toString()}
        renderOption={(props, option) => (
          <li {...props}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="img"
                src={option.image.url}
                alt={option.image.alt}
                sx={{ height: 25, width: 25, objectFit: 'contain' }}
              />

              <Typography sx={{ fontSize: 14 }}>{option.label}</Typography>
            </Stack>
          </li>
        )}
      />
    </OrdersFiltersMenuV2Inputs>
  );
};

export default OrdersFiltersMenuV2VariantFinalized;
